import { useEffect, useState } from "react";
import { TreeNode } from "../components/tree/Tree";
import {
    AcctRenovationPackagesQuery,
    RevenueModelType,
    useAcctRenovationPackagesLazyQuery,
    useGetUnitsCountLazyQuery,
    VersionType
} from "../__generated__/generated_types";
import { BudgetingType } from "../BudgetingType";
import { propertyHasRevenueModel, useProperties } from "../contexts/properties/PropertiesContext";

export type UseRenovationsWorkflowProps = {
    categoryType?:string,
    budgetYear: number,
}

export type UseRenovationsWorkflowReturn = {
    renovationsWorkflow: TreeNode[],
    renovationsWorkflowDataLoading: boolean;
}

export function useRenovationsWorkflow(props: UseRenovationsWorkflowProps):UseRenovationsWorkflowReturn{
    // NOTE: The way the "renovationsWorkflowDataLoading" is done is fiddly. It has to be perfect in the
    // sense that it must be true while any calculation or data fetching is being done to produce
    // for "renovationsWorkflow".
    // IF YOU DEVIATE FROM THIS, YOU WILL BREAK NAVIGATION TO RENOVATIONS. ENJOY!!!

    const { currentProperty } = useProperties();

    const [renovationsWorkflow, setRenovationsWorkflow] = useState<Array<TreeNode>>([]);
    const [renovationsWorkflowDataLoading, setRenovationsWorkflowDataLoading] = useState(true);

    const [getRenovationsData, {data: renovationsData, loading: renovationsDataLoading, error: renovationsError}] = useAcctRenovationPackagesLazyQuery({
        fetchPolicy: "no-cache"
    });
    const [renovationData, setRenovationData] = useState<AcctRenovationPackagesQuery | undefined>(undefined);
    const [getUnitsCount, { data: unitsCountData, loading: unitsCountLoading, error: unitsCountError}] = useGetUnitsCountLazyQuery({
        fetchPolicy: "no-cache"
    });

    useEffect(() => {
        if(!currentProperty || !propertyHasRevenueModel(currentProperty, RevenueModelType.Original)) {
           setRenovationsWorkflow([]);
           setRenovationsWorkflowDataLoading(false);
        } else {
            setRenovationsWorkflowDataLoading(true);
            getRenovationsData({
                variables: {
                    propertyIds: [currentProperty.id],
                    yearAndType: {
                        versionType: props.categoryType === BudgetingType.BUDGET ? VersionType.Budget : VersionType.Reforecast,
                        year: (props.categoryType === BudgetingType.BUDGET ? currentProperty?.budgetYear : currentProperty?.reforecastYear) ?? 0
                    },
                    budgetYear: props.budgetYear
                }
            });
            getUnitsCount({variables: {propertyId: currentProperty.id, budgetYear: props.budgetYear}});
        }
    }, [currentProperty, props.budgetYear]);

    useEffect(() => {
        if(renovationsData && !renovationsDataLoading) {
            setRenovationData(renovationsData);
        }
    }, [renovationsData, renovationsDataLoading]);

    const getRenovationWorkflow = (renoData: AcctRenovationPackagesQuery, unitTypes: {unitTypeId: string, unitTypeName: string}[]):TreeNode[] => {
        // Gather the list of units with packages
        const unitsWithPackages = new Set(renoData.queryRenovationPackages.map(pkg => pkg.unitTypeId));//Array.from();

        const treeNodeChildren:TreeNode[] = unitTypes.sort(
            (a, b)=>{ return a.unitTypeName.localeCompare(b.unitTypeName);}
        ).reduce(function(result:TreeNode[], unitType) {
            if (unitsWithPackages.has(unitType.unitTypeId)) {
                result.push({
                    title: unitType.unitTypeName,
                    subtitle: unitType.unitTypeId,
                    url: `/renovations/${unitType.unitTypeId}`,
                });
            }
            return result;
        }, []);

        // Renovation Schedule section has Unit Types as children
        return [{
            title: 'Renovations',
            children: treeNodeChildren,
        }];
    };

    useEffect(
        () => {
            if(!currentProperty) {
                return;
            }

            if(renovationsDataLoading || unitsCountLoading) {
                return;
            }

            if(currentProperty.unitLevelModelingEnabled || renovationsError || unitsCountError) {
                setRenovationsWorkflow([]);
                setRenovationsWorkflowDataLoading(false);
                return;
            }

            if(!renovationData || !unitsCountData) {
                return;
            }

            const renovationsWorkflow = getRenovationWorkflow(renovationData, unitsCountData.propertyUnitCounts);
            setRenovationsWorkflow(renovationsWorkflow);
            setRenovationsWorkflowDataLoading(false);
        },
        [currentProperty, renovationData, renovationsDataLoading, renovationsError, unitsCountData, unitsCountLoading, unitsCountError]
    );

    return {
        renovationsWorkflow,
        renovationsWorkflowDataLoading: renovationsWorkflowDataLoading,
    };
}
